import Spinner from "@/Components/Spinner/Spinner";
import AboutUs from "@/Components/WebsiteSettings/Settings/AboutUs";
import AccountRequirement from "@/Components/WebsiteSettings/Settings/AccountRequirement";
import ColorTemplate from "@/Components/WebsiteSettings/Settings/ColorTemplate";
import DeliveryMethods from "@/Components/WebsiteSettings/Settings/DeliveryMethods";
import Header from "@/Components/WebsiteSettings/Settings/Header";
import Images from "@/Components/WebsiteSettings/Settings/Images";
import Is18PlusWebsite from "@/Components/WebsiteSettings/Settings/Is18PlusWebsite";
import Languages from "@/Components/WebsiteSettings/Settings/Languages";
import PaymentMethods from "@/Components/WebsiteSettings/Settings/PaymentMethods";
import Sections from "@/Components/WebsiteSettings/Settings/Sections";
import SocialMedia from "@/Components/WebsiteSettings/Settings/SocialMedia";
import useCommon from "@/shared/hooks/useCommon";
import useFranchiseHook from "@/shared/hooks/useFranchiseHook";
import useGenerateImageUrl from "@/shared/hooks/useGenerateImageUrl";
import usePaymentHook from "@/shared/hooks/usePaymentHook";
import useThemesHook from "@/shared/hooks/useThemesHook";
import useWebsiteSettingsHook from "@/shared/hooks/useWebsiteSettingsHook";
import { getCloudinaryFileId, isAiImage } from "@/shared/imageLibrary/aiImage";
import { AccountRequirementEnum } from "@/shared/utils/enum";
import { asArray, errorToast } from "@/shared/utils/helpers";
import { useAtom } from "jotai";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { themeListAtom } from "../../../../shared/atoms";
import { supportedWebsiteLanguages } from "../../../../shared/utils/constant";
import { getFormData } from "../../../../shared/utils/helpers";

const deliveryMethodsEnum = {
  1: "delivery",
  2: "takeAway",
  3: "dineIn",
};

const defaultSectionList = [
  { label: "reviews", value: 1, name: "recencies", isActive: false },
  { label: "contact", value: 4, name: "contact", isActive: false },
  { label: "appBanner", value: 5, name: "appBanner", isActive: false },
];

export const imageType = {
  SLIDER_IMAGE: "slider-image",
  LOGO: "logo",
  NAV_IMAGE: "nav-image",
  MENU_HEADER: "menu_header",
  SLIDER: "slider",
};

export const DEFAULT_IMAGE_ID = "transpatentEmpty_sr5jbs";

export type TImageType =
  | "slider-image"
  | "logo"
  | "nav-image"
  | "about-us-image";

type TColorTemplate = {
  template_name: string;
  item_id: number;
};

const getLanguages = (languages: string | string[]) => {
  if (!languages || parseFloat(String(languages)) === 0) {
    return supportedWebsiteLanguages;
  }
  const selectedIds = asArray(languages).map(Number);
  const selected = selectedIds
    .map((id) => supportedWebsiteLanguages.find((l) => l.value === id))
    .filter(Boolean)
    .map((l) => ({ ...l, isActive: true }));
  const rest = supportedWebsiteLanguages
    .filter((l) => !selectedIds.includes(l.value))
    .map((l) => ({ ...l, isActive: false }));
  return [...selected, ...rest];
};

const getSectionList = (sections: string | string[]) => {
  const selectedIds = asArray(sections).map(Number);
  const selected = selectedIds
    .map((id) => defaultSectionList.find((item) => item.value === id))
    .filter(Boolean)
    .map((item) => ({ ...item, isActive: true }));
  const rest = defaultSectionList.filter(
    (item) => !selectedIds.includes(item.value)
  );
  return [...selected, ...rest];
};

interface SettingsFormProps {
  websiteSettingsData: any;
}

const SettingsForm = ({ websiteSettingsData }: SettingsFormProps) => {
  const tCommon = useCommon();
  const [franchiseId] = useFranchiseHook();
  const {
    postImageToCloudinary,
    postWebsiteSettings,
    uploadImageToCloudinary,
  } = useWebsiteSettingsHook({ franchiseId });
  const [themeList] = useAtom(themeListAtom);
  const { getPayment } = usePaymentHook();
  const generateImageUrl = useGenerateImageUrl();

  const getColorTemplate = (): TColorTemplate => {
    const item = themeList.find(
      (i) => i.item_id === +websiteSettingsData.color_template_id
    );
    return {
      template_name: item?.template_name,
      item_id: item?.item_id,
    };
  };

  const getImageUrls = (images: string | string[], type: string) =>
    asArray(images)
      .filter((image) => image && image !== DEFAULT_IMAGE_ID)
      .map((image) => generateImageUrl(image, type, franchiseId));

  const methods = useForm({
    defaultValues: {
      is18PlusWebsite: websiteSettingsData.is_18_plus_website ?? false,
      images: {
        sliderImages: getImageUrls(
          websiteSettingsData.home_sliders,
          imageType.SLIDER
        ),
        logo: getImageUrls(websiteSettingsData.logo, imageType.LOGO),
        backgroundImage: getImageUrls(
          websiteSettingsData.header_background_image,
          imageType.MENU_HEADER
        ),
      },
      accountRequirement:
        websiteSettingsData.account_requirement ??
        AccountRequirementEnum.NO_REQUIREMENT,
      colorTemplate: getColorTemplate(),
      languages: getLanguages(websiteSettingsData.languages),
      sectionList: getSectionList(websiteSettingsData.sections),
      deliveryMethods: asArray(
        websiteSettingsData.delivery_method_position
      ).map((id) => ({
        label: tCommon(deliveryMethodsEnum[id]),
        value: id,
      })),
      paymentMethods: asArray(websiteSettingsData.payment_method_position).map(
        (id) => ({
          label: tCommon(getPayment(Number(id))?.translationKey),
          value: id,
        })
      ),
      socialMediaUrls: (websiteSettingsData.social_media_urls ??
        []) as string[],
      about_us_content: "",
    },
  });
  const { handleSubmit, setValue } = methods;

  useEffect(() => {
    setValue("colorTemplate", getColorTemplate());
  }, [themeList]);

  const getSignData = async (type: TImageType) => {
    try {
      const signData = await postImageToCloudinary.mutateAsync(type);
      return signData;
    } catch (err) {
      throw new Error(err);
    }
  };

  const postImage = async (image: File | string, type: TImageType) => {
    if (image instanceof File) {
      const signData = await getSignData(type);
      const formData = getFormData(image, signData);
      const { data } = await uploadImageToCloudinary.mutateAsync({
        url: signData.upload_url,
        formData,
      });
      return `v${data?.version}/${
        data?.secure_url?.split("/")?.reverse()?.[0]
      }`;
    } else if (isAiImage(image)) {
      // AI images from the image library are linked by their file id.
      return getCloudinaryFileId(image);
    } else {
      const [fileId, , , , , version] = image.split("/").reverse();
      return `${version}/${fileId}`;
    }
  };

  const onSubmit = async ({
    is18PlusWebsite,
    images,
    accountRequirement,
    colorTemplate,
    languages,
    sectionList,
    deliveryMethods,
    paymentMethods,
    socialMediaUrls,
    about_us_content,
  }) => {
    if (about_us_content.includes('src="data:')) {
      errorToast("settings_page.aboutUsBase64Image");
      return;
    }

    const logo = await Promise.all(
      images.logo.map((image: File) =>
        postImage(image, imageType.LOGO as TImageType)
      )
    );
    const home_sliders = await Promise.all(
      images.sliderImages.map((image: File) =>
        postImage(image, imageType.SLIDER_IMAGE as TImageType)
      )
    );
    const header_background_image = await Promise.all(
      images.backgroundImage.map((image: File) =>
        postImage(image, imageType.NAV_IMAGE as TImageType)
      )
    );

    const submitData = {
      languages: languages.filter((l) => l.isActive).map((l) => l.value),
      logo: logo?.[0] ?? DEFAULT_IMAGE_ID,
      home_sliders: home_sliders?.length ? home_sliders : [DEFAULT_IMAGE_ID],
      header_background_image: header_background_image?.[0] ?? DEFAULT_IMAGE_ID,
      sections: sectionList
        .map((i) => {
          if (i.isActive) return i.value;
        })
        .filter(Boolean),
      payment_method_position: paymentMethods.map((i) => i.value),
      delivery_method_position: deliveryMethods.map((i) => i.value),
      color_template_id: colorTemplate.item_id,
      is_18_plus_website: is18PlusWebsite,
      account_requirement: accountRequirement,
      social_media_urls: socialMediaUrls.map((url) => url ?? ""),
      about_us_content,
    };

    await postWebsiteSettings.mutateAsync({
      data: submitData,
      restaurantSiteSettingsID: websiteSettingsData.item_id,
    });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Header
          isPending={
            postWebsiteSettings.isPending ||
            postImageToCloudinary.isPending ||
            uploadImageToCloudinary.isPending
          }
        />
        <Is18PlusWebsite />
        <Images />
        <AccountRequirement />
        <ColorTemplate />
        <Languages />
        <Sections />
        <DeliveryMethods />
        <PaymentMethods />
        <AboutUs defaultValue={websiteSettingsData.about_us_content} />
        <SocialMedia />
      </form>
    </FormProvider>
  );
};

const Settings = () => {
  useThemesHook();
  const [franchiseId] = useFranchiseHook();
  const { websiteSettingsData, websiteSettingsQuery } = useWebsiteSettingsHook({
    franchiseId,
  });

  useEffect(() => {
    websiteSettingsQuery.refetch();
  }, [franchiseId]);

  return (
    <div>
      {websiteSettingsQuery.isFetching && (
        <div className="mt-60 flex justify-center">
          <Spinner size="0.8rem" />
        </div>
      )}
      {!websiteSettingsQuery.isFetching && websiteSettingsQuery.isError && (
        <div className="mt-60 flex w-full justify-center">error</div>
      )}

      {!websiteSettingsQuery.isFetching &&
        websiteSettingsQuery.isSuccess &&
        websiteSettingsData && (
          <SettingsForm websiteSettingsData={websiteSettingsData} />
        )}
    </div>
  );
};

export default Settings;
